import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, LineElement);

const ChartWrapper = styled.div`
  position: relative;
  background-color: #f9f9f9;
  width: 100%;
  height: 65vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 15px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
`;

const ZakupyCompanyAnalysis = ({ data }) => {
  const [vendorData, setVendorData] = useState({});
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const grouped = data.reduce((acc, row) => {
      const year = parseInt(row.Rok);
      const month = parseInt(row.Miesiac);
      const vendor = row.NazwaDostawcy;

      if (!acc[year]) acc[year] = Array.from({ length: 12 }, () => new Set());
      acc[year][month - 1].add(vendor);

      return acc;
    }, {});

    const years = Object.keys(grouped).map(Number).sort((a, b) => b - a);
    const latestYear = years[0];

    setVendorData(grouped);
    setAvailableYears(years);
    setSelectedYear(latestYear);
  }, [data]);

  useEffect(() => {
    if (!selectedYear || !vendorData[selectedYear]) return;

    const monthlyVendors = vendorData[selectedYear].map((set) => set.size);
    const variation = monthlyVendors.map((val, idx) =>
      idx === 0 ? 0 : ((val - monthlyVendors[idx - 1]) / monthlyVendors[idx - 1]) * 100
    );

    setChartData({
      labels: [
        "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
        "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
      ],
      datasets: [
        {
          type: "bar",
          label: "Unikalni Dostawcy",
          data: monthlyVendors,
          backgroundColor: "#9848c7",
          borderColor: "#9848c7",
          borderWidth: 1,
        },
        {
          type: "line",
          label: "Zmiana m/m (%)",
          data: variation,
          borderColor: "rgba(255, 99, 132, 1)",
          backgroundColor: "rgba(255, 99, 132, 0.2)",
          fill: true,
          tension: 0.4,
          yAxisID: "percentage",
        },
      ],
    });
  }, [selectedYear, vendorData]);

  return (
    <ChartWrapper>
      <InfoIconWrapper data-tooltip-id="zakupy-company-tooltip">
        <Info />
      </InfoIconWrapper>

      <InfoTooltip
        id="zakupy-company-tooltip"
        place="left"
        content={
          <>
            <strong>Analiza Dostawców</strong>
            <br />
            Liczba unikalnych firm dostarczających<br />
            w danym miesiącu + zmiana m/m.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label htmlFor="year-select">Rok:&nbsp;</label>
          <select
            id="year-select"
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </FiltersWrapper>

      {chartData && (
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: {
                title: {
                  display: true,
                  text: "Miesiąc",
                },
              },
              y: {
                title: {
                  display: true,
                  text: "Liczba Dostawców",
                },
                beginAtZero: true,
              },
              percentage: {
                type: "linear",
                position: "right",
                title: {
                  display: true,
                  text: "Zmiana m/m (%)",
                },
                grid: {
                  drawOnChartArea: false,
                },
              },
            },
            plugins: {
              title: {
                display: true,
                text: `Ilość dostawców ${selectedYear}`,
              },
              tooltip: {
                callbacks: {
                  label: (ctx) =>
                    ctx.dataset.type === "line"
                      ? `Zmiana: ${ctx.raw.toFixed(1)}%`
                      : `Dostawcy: ${ctx.raw}`,
                },
              },
            },
          }}
        />
      )}
    </ChartWrapper>
  );
};

export default ZakupyCompanyAnalysis;
